import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import _cloneDeep from 'lodash-es/cloneDeep';

import { PropalService } from 'app_locobook/service/propal/propal.service';
import { ISamAgent } from "libraries/service/resources/sam/ISamAgent";
import {
    IAirCreateBookingRequestPayment,
    IAirCreateBookingRequestSegment,
} from 'libraries/service/resources/model/booking/air/IAirCreateBooking';
import {
    IAirQuotedAvailabilityResponse
} from "libraries/service/resources/model/booking/air/IAirQuotedAvailabilityResponse";
import { ISamCompany } from "libraries/service/resources/sam/ISamCompany";
import { IAirTotal } from "libraries/service/resources/model/search/air/IAirTotal";
import {
    IAirGetQuotedAvailabilityRequest, IAirQuotedAvailabilityTraveller
} from "libraries/service/resources/model/booking/air/IAirQuotedAvailability";
import { Observable, of } from "rxjs";
import { IWenji } from "libraries/interfaces/IWenji";
import { ResponseForConvertOfferAfx2jsonType } from '@afidium/ts-facade';
import { flatMap } from 'rxjs/internal/operators';
import { CacheQuotedAvailabilityService } from './cache-quoted-availability/cache-quoted-availability.service';
import { CacheSamResourceService } from 'libraries/service/resources/sam/cache-sam-resource.service';
import { AirQuoteMappingService } from "libraries/service/resources/airQuote/air-quote-mapping.service";
import { AirQuoteResourceService } from "libraries/service/resources/airQuote/airQuoteResource.service";
import { IAirExternalBooking } from "libraries/service/resources/model/booking/air/IAirExternalBooking";
import { LowCostCompanyService } from "./low-cost-company.service";
import { ICodeName } from "libraries/service/resources/model/shared/ICodeName";
import { AgentAgencyService } from "./agent-agency.service";

declare var wenji: IWenji;

@Injectable()
export class QuotedAvailabilityService {

    private propalResponse: any;

    constructor(
        private readonly airQuoteMappingService: AirQuoteMappingService,
        private readonly airQuoteResourceService: AirQuoteResourceService,
        private readonly propalService: PropalService,
        private readonly cacheQuotedAvailabilityService: CacheQuotedAvailabilityService,
        private readonly cacheSamResourceService: CacheSamResourceService,
        public readonly lowCostCompanyService: LowCostCompanyService,
        private readonly agentAgencyService: AgentAgencyService
    ) {
        this.propalResponse = propalService.propalResponse;
    }

    public initPropalResponse(): void {
        this.propalResponse = this.propalService.propalResponse;
    }

    public buildRequest(
        bookingKey: string,
        selectedJouneys: string[],
        agent?: ISamAgent,
        company?: ISamCompany,
        locator?: string,
        isLowcost?: boolean,
        externalBooking?: IAirExternalBooking,
        vendor?: object,
    ): IAirGetQuotedAvailabilityRequest {
        const tripKey: string = this.propalResponse.data.key;
        const booking: any = this.propalResponse.data.booking[bookingKey];
        if (typeof vendor === 'string') {
            vendor = {code: vendor};
        }
        const request: IAirGetQuotedAvailabilityRequest = {
            tripKey,
            bookingKey,
            journeySelectedArray: selectedJouneys,
            transactionId: booking.transactionId,
            price: this.getPrice(booking),
            ...this.agentAgencyService.buildAgentAndAgency(agent, company, this.propalResponse),
            host: booking.host,
            segment: this.getSegment(booking),
            traveller: this.getTravellers(this.propalResponse.traveller, booking.traveller),
            keyword: booking.keyword,
            locator: locator || null,
            payment: isLowcost ? this.getPayment() : null,
            externalBooking,
            vendor
        };

        return request;
    }

    public sendRequest(request: IAirGetQuotedAvailabilityRequest, bookingKey: string, alternativeOfferXId?: string):
        Observable<{ request: IAirGetQuotedAvailabilityRequest, response: IAirQuotedAvailabilityResponse }> {
        const target: string = wenji.ui.target;

        return this.cacheQuotedAvailabilityService.getCacheQuotedAvailability(request, {bookingKey})
            .pipe(
                map((response: ResponseForConvertOfferAfx2jsonType): IAirQuotedAvailabilityResponse => {
                    return this.airQuoteMappingService.mapGqaNewToOld(response, alternativeOfferXId) as unknown as IAirQuotedAvailabilityResponse;
                }),
                map((response: IAirQuotedAvailabilityResponse)
                    : { request: IAirGetQuotedAvailabilityRequest, response: IAirQuotedAvailabilityResponse } => {
                    this.cacheQuotedAvailabilityService.updateRequest(bookingKey, {
                        ...request,
                        transactionId: response.transactionId
                    });
                    return {request, response};
                })
            );
    }

    public sendRequestWithService(request: IAirGetQuotedAvailabilityRequest, bookingKey: string, storeRequestBeforeCreateBooking?: boolean):
        Observable<ResponseForConvertOfferAfx2jsonType> {
        const target: string = wenji.ui.target;

        return this.cacheQuotedAvailabilityService
            .getCacheQuotedAvailabilityWithService(request, {bookingKey}, storeRequestBeforeCreateBooking);
    }

    public getQuotedAvailabilityStream(
        bookingKey: string,
        selectedJourneys?: string[],
        existingGQAWithServiceSelection?: any,
        sessionPostOnUpdateBooking?: any
    ): Observable<ResponseForConvertOfferAfx2jsonType> {
        if (existingGQAWithServiceSelection) {
            return of(existingGQAWithServiceSelection);
        }
        const journeysSelection: string[] = selectedJourneys || this.propalResponse.display.selectedJourneys;
        const userInfo: {
            sam_company: number;
            sam_contact: { id: number; };
        } = JSON.parse(sessionStorage.getItem("userInformations"));
        const userId: number = userInfo.sam_contact.id;
        const companyId: number = userInfo.sam_company;
        const locator: string = this.propalService.propalResponse.searchRQ?.body?.locator || null;
        const isLowCost: boolean = this.lowCostCompanyService.hasLcc(this.propalResponse.data.booking[bookingKey], this.propalResponse.display.selectedJourneys);
        let vendor = null;
        let externalBooking: IAirExternalBooking = null;
        if (isLowCost) {
            vendor = this.propalResponse.data.booking[bookingKey].vendor;
            const dictionaryVendor: ICodeName = (typeof vendor === 'string') && this.propalResponse.data?.dictionary?.carrier ?
                this.propalResponse.data?.dictionary?.carrier[vendor] :
                vendor;
            vendor = dictionaryVendor ?? vendor;
            externalBooking = sessionPostOnUpdateBooking?.externalBooking;
        }
        return this.cacheSamResourceService.getAgentAndCompany(userId, companyId)
            .pipe(
                map(([samAgent, samCompany]: [ISamAgent, ISamCompany]): IAirGetQuotedAvailabilityRequest => {
                    return this.buildRequest(bookingKey, journeysSelection, samAgent, samCompany, locator, isLowCost, externalBooking, vendor);
                }),
                flatMap((request: IAirGetQuotedAvailabilityRequest)
                    : Observable<ResponseForConvertOfferAfx2jsonType> => {
                    this.cacheQuotedAvailabilityService.updateRequest(bookingKey, request);
                    return this.cacheQuotedAvailabilityService
                        .getCacheQuotedAvailability(request, {bookingKey});
                })
            );
    }


    public getTravellers(propalResponseTravellers: any, bookingTravellers: any): IAirQuotedAvailabilityTraveller[] {

        const travellersArg: any = propalResponseTravellers && Object.keys(propalResponseTravellers)?.length ?
            propalResponseTravellers :
            bookingTravellers;

        const travellerValues: any[] = Object.values(travellersArg);

        return travellerValues
            .map((traveller: any): IAirQuotedAvailabilityTraveller => {
                return {
                    id: traveller.id ? traveller.id : traveller.xId || null,
                    xId: traveller.xId ? traveller.xId : traveller.id || null,
                    type: traveller.type,
                    title: traveller.title ? traveller.title : null,
                    firstName: traveller.firstName ? traveller.firstName : null,
                    lastName: traveller.lastName ? traveller.lastName : null
                };
            })
            .sort((travellerA: IAirQuotedAvailabilityTraveller, travellerB: IAirQuotedAvailabilityTraveller): number => {
                return travellerA.type.localeCompare(travellerB.type);
            });
    }

    /**
     * get segments data for RQ
     * needs : simplify some datas
     */
    public getSegment(booking: any): IAirCreateBookingRequestSegment {
        const bookingClone = _cloneDeep(booking);
        const output: IAirCreateBookingRequestSegment = {};

        const nbSegment = this.propalResponse.display.segments.length;


        for (let i = 0; i < nbSegment; i++) {

            //transfer selected journey Data in segment root
            const segmentKey: string = this.propalResponse.display.segments[i];
            const journeyKey: string = this.propalResponse.display.selectedJourneys[i];
            // tslint:disable-next-line:typedef
            const bookingElement = bookingClone.segment[segmentKey] ?
                bookingClone.segment[segmentKey] :
                Object.values(bookingClone.segment)[i];
            const journeyValue = bookingClone.segment[segmentKey] ?
                bookingClone.segment[segmentKey][journeyKey] :
                Object.values(bookingClone.segment)[i][journeyKey];
            journeyValue.selectedJourneyKey = journeyKey;
            output[segmentKey] = journeyValue;

            //retrive syscode
            const bookingJourneyArray = Boolean(bookingElement.journeyArray) ?
                Array.isArray(bookingElement.journeyArray) ? bookingElement.journeyArray : [bookingElement.journeyArray] : null;
            const journeySyscode: string = bookingJourneyArray?.length ? bookingJourneyArray.find((journeyItem): boolean => journeyItem.xRef === journeyKey).syscode : null;
            //simplify root datas
            if (journeySyscode) {
                output[segmentKey].syscode = journeySyscode;
            }
            output[segmentKey].stop = output[segmentKey].sector.length - 1;
            output[segmentKey].xId = bookingClone.segment[segmentKey] ?
                bookingClone.segment[segmentKey].xId :
                // @ts-ignore
                Object.values(bookingClone.segment)[i].xId;


            //simplify sectors data
            let j = 0;
            for (const sectorItem of journeyValue.sector) {
                if (sectorItem.carrier) {
                    sectorItem.carrier = sectorItem.carrier.code;
                }
                if (sectorItem.seat) {
                    sectorItem.seat = Object.keys(sectorItem.seat)[0];
                }

                output[segmentKey].sector[j] = sectorItem;

                j++;
            }

        }

        //retrive combinability
        const combinabilityArray: any[] = bookingClone.combinabilityArray;
        const outputValues: any[] = Object.values(output);
        const outputKeys: string[] = Object.keys(output);
        if(combinabilityArray?.length){
            const relatedCombinability: any = combinabilityArray
                .find((combinability: any): boolean => {

                    return combinability.journeyArray
                        .map((journey: any, index: number): boolean => {
                            // @ts-ignore
                            return journey.xRef.includes(outputValues[index].selectedJourneyKey);
                        })
                        .filter((item: boolean): boolean => item)
                        .length === Object.values(output).length;
                });
            outputKeys.forEach((key: string, index: number): void => {
                output[key] = {
                    ...output[key],
                    syscode : output[key].syscode ?
                        `${relatedCombinability.journeyArray[index].syscode}_${output[key].syscode}` :
                        relatedCombinability.journeyArray[index].syscode,
                };
            });
        }

        return output;
    }


    /**
     *  get Price for RQ
     **/
    private getPrice(booking: any): any {
        const output: any = {};

        output.role = "Total";
        output.fareType = booking.fareType || "Public";
        const hasRetailPrice: boolean = booking.total.some((totalItem: IAirTotal): boolean => totalItem.xType === "retail");
        const value: number = booking.total.find((totalItem: IAirTotal): boolean => {
            const xType: "retail" | "cost" = hasRetailPrice ? "retail" : "cost";
            return totalItem.xType === xType;
        }).value;
        output.value = value;
        output.total = value;

        return output;
    }

    /**
     *  get payment for RQ
     **/
    private getPayment(): IAirCreateBookingRequestPayment {
        return {
            formOfPayment: {
                card: {
                    xIs: "VirtualCard"
                },
                ...this.propalResponse?.searchRQ?.body?.payment?.formOfPayment
            }
        };
    }
}
